import React, {
  forwardRef,
  useEffect,
  useState,
  useRef,
  useMemo,
  memo
} from "react";
import { connect } from "react-redux";
import { Dialog, TextField, Tooltip } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import makeStyles from "@material-ui/core/styles/makeStyles";
import MaterialTable, { MTableBody } from "material-table";

import ArrowUpward from "@material-ui/icons/ArrowUpward";
import Check from "@material-ui/icons/Check";
import Clear from "@material-ui/icons/Clear";
import Delete from "@material-ui/icons/Delete";
import Edit from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import CustomActions from "./CustomActions";
import { setInfoDialog } from "../../../store/actions/common";
import { arraysOfObjectsEqual } from "../../../utils/helpers";
import { numberValidationRegex } from "../../../utils/regExps";

const tableIcons = {
  Add: forwardRef(() => (
    <>
      <AddIcon fontSize="small" color="primary" />

      <Typography variant="body2" color="primary" component="span">
        Add unit
      </Typography>
    </>
  )),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <Delete {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />)
};

const useStyles = makeStyles(theme => ({
  paper: {
    width: 904,
    maxWidth: 904,
    height: 600,
    maxHeight: 600
  },
  actions: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    marginTop: "auto",
    padding: theme.spacing(1.5, 3),

    "& button": {
      marginLeft: 0,
      lineHeight: "1rem",
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25)
    },

    "& button:last-of-type": {
      marginLeft: theme.spacing(0),
      border: "1px solid #DDDDDD"
    }
  },
  tableWrapper: {
    "& > .MuiPaper-elevation2": {
      boxShadow: "none"
    },

    "& .MuiIconButton-root:hover": {
      backgroundColor: "#EEE"
    },

    "&  .MuiToolbar-regular": {
      padding: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      height: 72,
      boxSizing: "border-box",

      "& button": {
        borderRadius: theme.spacing(0.5),
        paddingRight: theme.spacing(1.5),

        "& .MuiTypography-root": {
          marginLeft: theme.spacing(1),
          fontWeight: 500
        },

        "&:hover": {
          backgroundColor: "#F3F4FF"
        }
      },

      "& h6": {
        fontSize: "1.25rem",
        fontWeight: 500
      },

      "& + div": {
        maxHeight: 440
      }
    },

    "& .MuiTable-root": {
      tableLayout: "fixed",

      "& thead tr": {
        height: 42
      },

      "& tbody": {
        overflowY: "auto",

        "& tr": {
          height: 48,
          "&:hover": {
            backgroundColor: "#eee"
          },
          "&[mode=update], &[mode=delete], &[mode=add]": {
            background: "none"
          }
        }
      },

      "& tr:first-of-type th": {
        borderColor: "#c4c4c4"
      },

      "& th": {
        fontSize: "0.75rem",
        color: "#808080",
        textAlign: "left !important"
      },

      "& td, & input": {
        fontSize: "0.9375rem",
        color: "#202124 !important"
      },

      "& td input::placeholder": {
        opacity: 0
      },

      "& td, & th": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        borderColor: "#eee",
        padding: theme.spacing(0, 0, 0, 1),

        "&:first-child": {
          width: "140px",
          paddingLeft: theme.spacing(2),

          "& > div": {
            paddingLeft: theme.spacing(1.25)
          }
        },

        "&:nth-child(2)": {
          paddingRight: theme.spacing(2),
          width: "110px"
        },

        "&:nth-child(3)": {
          paddingRight: theme.spacing(2),
          width: "116px"
        },

        "&:nth-child(4)": {
          paddingRight: theme.spacing(2),
          width: "118px"
        },

        "&:nth-child(5)": {
          paddingRight: theme.spacing(2),
          width: "74px"
        },

        "&:last-child": {
          width: "192px",
          paddingRight: theme.spacing(3)
        }
      },

      "& td h6": {
        fontSize: theme.typography.body1.fontSize,
        fontWeight: 400
      }
    }
  },
  text15pt: {
    fontSize: "0.9375rem"
  },
  tooltip: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    lineHeight: "0.9375rem",
    fontWeight: "normal",
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.24)",
    padding: theme.spacing(1.25),
    maxWidth: "172px"
  },
  iconButton: {
    marginRight: theme.spacing(2),
    borderRadius: "50% !important",
    paddingRight: `${theme.spacing(1)}px !important`,
    "& svg": {
      width: theme.spacing(3),
      height: theme.spacing(3)
    },
    "&:hover": {
      backgroundColor: "#eee !important"
    }
  }
}));

// Custom TableBody component to track edit changes
const CustomBody = props => {
  useEffect(() => {
    props.setEditing(props.hasAnyEditingRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.hasAnyEditingRow]);

  return <MTableBody {...props} />;
};

function UnitsDialog({
  open,
  setUnitsDialog,
  units,
  setUnits,
  load,
  stopId,
  taskStatus,
  isDisabledToEdit
}) {
  const classes = useStyles();
  const [isEditing, setEditing] = useState(false);
  const handleClose = () => setUnitsDialog(false);
  const quantityInputRef = useRef();

  useEffect(() => {
    if (stopId) {
      let newData = [...units];

      if (!load) {
        setUnits([]);
      } else if (units.length === 1) {
        newData[0].quantity = +load;
        setUnits(newData);
      } else if (!units.length && load) {
        setUnits([{ name: "", barcode: "", note: "", quantity: load }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, stopId]);

  // Populate if empty
  useEffect(() => {
    !units.length && setUnits([{ name: "", barcode: "", note: "" }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopId]);

  useEffect(() => {
    if ((taskStatus === "finished" || taskStatus === "skipped") && open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStatus]);

  const totalWeight = useMemo(
    () =>
      units.reduce((acc, item) => {
        return acc + (item.weight || 0);
      }, 0),
    [units]
  );

  const totalVolume = useMemo(
    () =>
      units.reduce((acc, item) => {
        return acc + (item.volume || 0);
      }, 0),
    [units]
  );
  const handleKeyDown = e => {
    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
  };

  const handleChange = (e, props) => {
    if (Number.isInteger(+e.target.value)) {
      if (+e.target.value > 0) {
        props.onChange(+e.target.value);
      } else {
        props.onChange("");
      }
    } else {
      props.onChange(props.value);
    }
  };

  const handlePaste = e => {
    const pastedData = e.clipboardData.getData("Text");
    if (numberValidationRegex.test(pastedData)) {
      e.preventDefault();
    }
  };

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      onClose={handleClose}
      PaperProps={{ className: classes.paper }}
      scroll={"paper"}
    >
      <Box className={classes.tableWrapper}>
        <MaterialTable
          components={{
            Body: props => CustomBody({ ...props, setEditing }),
            Actions: props => {
              return (
                <CustomActions
                  unitDialog
                  isValidQuantity={quantityInputRef?.current?.value > 0}
                  {...props}
                />
              );
            }
          }}
          icons={tableIcons}
          style={{ overflowY: "auto" }}
          options={{
            search: false,
            paging: false,
            headerStyle: {
              backgroundColor: "#F7F7F7",
              borderBottom: "1px solid #EEE",
              borderTop: "1px solid #EEE"
            },
            draggable: false
          }}
          columns={[
            {
              title: `Unit Count ${load ? `(${load})` : ""}`,
              field: "quantity",
              emptyValue: 1,
              editComponent: props => {
                return (
                  <TextField
                    type="number"
                    margin="dense"
                    value={props.value || ""}
                    autoFocus
                    inputRef={quantityInputRef}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onChange={e => handleChange(e, props)}
                    id="unitsDialog_unitCountInput"
                  />
                );
              }
            },
            {
              title: `Unit Weight  ${totalWeight ? `(${totalWeight})` : ""}`,
              field: "weight",
              editComponent: props => {
                return (
                  <TextField
                    type="number"
                    margin="dense"
                    value={props.value || ""}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onChange={e => handleChange(e, props)}
                    id="unitsDialog_unitWeightInput"
                  />
                );
              }
            },
            {
              title: `Unit Volume  ${totalVolume ? `(${totalVolume})` : ""}`,
              field: "volume",
              editComponent: props => {
                return (
                  <TextField
                    type="number"
                    margin="dense"
                    value={props.value || ""}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onChange={e => handleChange(e, props)}
                    id="unitsDialog_unitVolumeInput"
                  />
                );
              }
            },
            { title: "Unit ID", field: "name" },
            {
              title: "Unit Note",
              field: "note",
              render: rowData => (
                <Tooltip
                  title={rowData.note}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <Typography className={classes.text15pt} noWrap>
                    {rowData.note}
                  </Typography>
                </Tooltip>
              ),
              editComponent: props => (
                <TextField
                  defaultValue={props.value || ""}
                  onPaste={handlePaste}
                  onChange={e => props.onChange(e.target.value)}
                  multiline
                />
              )
            }
          ]}
          data={units}
          localization={{
            header: {
              actions: ""
            }
          }}
          title={
            <Box alignSelf="center" alignItems="center" display="flex">
              <IconButton
                onClick={handleClose}
                className={classes.iconButton}
                disabled={isEditing}
              >
                <CloseIcon />
              </IconButton>
              Edit unit details
            </Box>
          }
          editable={
            !isDisabledToEdit && {
              onRowAdd: newItem =>
                new Promise((resolve, reject) => {
                  if (units.length < 20) {
                    setUnits([...units, { ...newItem }], true);
                  }

                  resolve();
                }),
              onRowUpdate: (newItem, oldItem) =>
                new Promise((resolve, reject) => {
                  const newData = [...units];
                  const index = units.indexOf(oldItem);

                  newData[index] = {
                    ...newItem,
                    quantity: newItem.quantity ? +newItem.quantity : 1,
                    weight: newItem.weight ? +newItem.weight : "",
                    volume: newItem.volume ? +newItem.volume : ""
                  };

                  setUnits(newData, true);
                  resolve();
                }),
              onRowDelete: item =>
                new Promise((resolve, reject) => {
                  setUnits(
                    units.filter(el => el !== item),
                    true
                  );

                  resolve();
                })
            }
          }
        />
      </Box>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} color="primary" disabled={isEditing}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function areEqual(prevProps, nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */

  return (
    prevProps.taskStatus === nextProps.taskStatus &&
    prevProps.taskId === nextProps.taskId &&
    prevProps.open === nextProps.open &&
    prevProps.load === nextProps.load &&
    arraysOfObjectsEqual(prevProps.units, nextProps.units)
  );
}

const mapStateToProps = state => ({});

export default connect(mapStateToProps, { setInfoDialog })(
  memo(UnitsDialog, areEqual)
);
